<script setup lang="ts">
import { computed, ref, watch, type VNodeChild } from 'vue'
import { X } from '@lucide/vue'
import RrButton from '../button/RrButton.vue'
import RenderContent from '../internal/RenderContent'
import { useScrollLock } from '../../composables/useScrollLock'
import { useOverlayFocusTrap } from '../../composables/useOverlayFocusTrap'
import { useUiConfig } from '../config-provider/context'
import type { ButtonType } from '../../types'

import type { ModalProps } from './types'

const props = withDefaults(defineProps<ModalProps>(), { open: false, getContainer: 'body', footer: undefined, title: '', description: '', width: 520, closable: true, mask: true, maskClosable: true, keyboard: true, confirmLoading: false, okType: 'primary', showFooter: true, showCancel: true, showOk: true, destroyOnClose: false, forceRender: false, centered: false })
defineOptions({ inheritAttrs: false })
const emit = defineEmits<{ 'update:open': [open: boolean]; cancel: [event?: Event]; ok: [event: MouseEvent]; afterClose: [] }>()
const config = useUiConfig()
const isOpen = computed(() => props.open)
const rendered = ref(props.open || props.forceRender)
watch([isOpen, () => props.forceRender], ([open, force]) => { if (open || force) rendered.value = true }, { flush: 'sync' })
const container = computed(() => {
  if (props.getContainer === false) return false
  if (typeof props.getContainer === 'function') return props.getContainer()
  if (typeof props.getContainer === 'string') return props.getContainer
  return props.getContainer ?? 'body'
})
const modalRef = ref<HTMLElement | null>(null)
useScrollLock(isOpen)
function handleClose(event?: Event) { if (!isOpen.value) return; emit('update:open', false); emit('cancel', event) }
const { zIndex } = useOverlayFocusTrap(isOpen, modalRef, { closeOnEscape: handleClose, escapeEnabled: () => props.keyboard, zIndex: () => props.zIndex })
function afterLeave() {
  if (isOpen.value) return
  if (props.destroyOnClose && !props.forceRender) rendered.value = false
  props.afterClose?.()
  emit('afterClose')
}
const computedWidth = computed(() => typeof props.width === 'number' ? `${props.width}px` : /^\d+$/.test(props.width) ? `${props.width}px` : props.width)
</script>

<template>
  <Teleport v-if="rendered" :to="container === false ? 'body' : container" :disabled="container === false">
    <div class="rr-modal-root" :style="{ zIndex }">
      <Transition name="rr-fade" appear><div v-if="isOpen && mask" class="rr-modal-mask" aria-hidden="true" @click="maskClosable && handleClose($event)" /></Transition>
      <Transition name="rr-zoom-big" appear @after-leave="afterLeave">
        <div v-show="isOpen" class="rr-modal-wrap" :class="{ 'is-centered': centered }">
          <section ref="modalRef" v-bind="$attrs" class="rr-modal" role="dialog" :aria-modal="mask || undefined" :aria-label="title || undefined" tabindex="-1" :style="{ width: `min(${computedWidth}, calc(100vw - 32px))` }">
            <header v-if="title || $slots.title || description || closable" class="rr-modal__header">
              <div class="rr-modal__title-area"><slot name="title"><h3 v-if="title" class="rr-modal__title">{{ title }}</h3></slot><p v-if="description" class="rr-modal__description">{{ description }}</p></div>
              <button v-if="closable" type="button" class="rr-modal__close-btn" data-overlay-close="true" title="关闭" aria-label="关闭" @click="handleClose"><X :size="17" aria-hidden="true" /></button>
            </header>
            <div class="rr-modal__body"><slot /></div>
            <footer v-if="footer !== null && (showFooter || $slots.footer)" class="rr-modal__footer">
              <slot name="footer">
                <RenderContent v-if="footer !== undefined" :value="footer" />
                <template v-else>
                  <RrButton v-if="showCancel" v-bind="cancelButtonProps" type="default" @click="handleClose">{{ cancelText ?? config.locale?.Modal?.cancelText ?? '取消' }}</RrButton>
                  <RrButton v-if="showOk" :type="okType" :loading="confirmLoading" v-bind="okButtonProps" @click="emit('ok', $event)">{{ okText ?? config.locale?.Modal?.okText ?? '确定' }}</RrButton>
                </template>
              </slot>
            </footer>
          </section>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style scoped>
.rr-modal-root {
  position: relative;
  z-index: var(--rr-z-dialog, 900);
}

.rr-modal-mask {
  position: fixed;
  inset: 0;
  z-index: var(--rr-z-dialog, 900);
  background: rgba(15, 17, 21, 0.45);
  backdrop-filter: blur(2px);
}

.rr-modal-wrap {
  position: fixed;
  inset: 0;
  z-index: var(--rr-z-dialog, 900);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 48px 16px;
  overflow: auto;
  pointer-events: none;
}

.rr-modal-wrap.is-centered {
  align-items: center;
}

.rr-modal {
  pointer-events: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: min(88vh, 860px);
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-overlay);
  overflow: hidden;
}

.rr-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 52px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--rr-color-border);
}

.rr-modal__title-area {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.rr-modal__title {
  margin: 0;
  color: var(--rr-color-text-strong);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

.rr-modal__description {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.rr-modal__close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  outline: 0;
  border-radius: var(--rr-radius-control);
  background: transparent !important;
  color: var(--rr-color-text-muted) !important;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background-color var(--rr-motion-duration-fast) var(--rr-motion-ease-out),
    color var(--rr-motion-duration-fast) var(--rr-motion-ease-out);
}

.rr-modal__close-btn:hover {
  background: var(--rr-color-surface-subtle) !important;
  color: var(--rr-color-text-strong) !important;
}

.rr-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
  overscroll-behavior: contain;
  color: var(--rr-color-text);
  font-size: 13px;
}

.rr-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
}
</style>
